/*
[GNU Affero General Public License (AGPL)](https://www.gnu.org/licenses/agpl-3.0.html)

VeloceUI - A UI component library for Angular v17+ made with care by Colibryx Srl in Italy.
Copyright (C) 2025 Colibryx S.r.l.

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as published by
the Free Software Foundation, either version 3 of the License, or
(at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

This license applies to any accompanying files located directly in the same directory.
*/

import {
	Component,
	EventEmitter,
	Output,
	ViewEncapsulation,
	forwardRef,
	input,
} from "@angular/core";
import {
	type ControlValueAccessor,
	FormsModule,
	NG_VALUE_ACCESSOR,
} from "@angular/forms";
import { AutosizeModule } from "ngx-autosize";
import { TooltipModule } from "../floating/tooltip/tooltip.module";
import { IconComponent } from "../icon/icon.component";

const noop = () => {};

const CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR = {
	provide: NG_VALUE_ACCESSOR,
	useExisting: forwardRef(() => TextareaComponent),
	multi: true,
};

@Component({
	selector: "cb-textarea",
	standalone: true,
	imports: [AutosizeModule, IconComponent, FormsModule, TooltipModule],
	templateUrl: "./textarea.component.html",
	styleUrl: "./textarea.component.scss",
	providers: [CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR],
	encapsulation: ViewEncapsulation.None,
	// changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TextareaComponent implements ControlValueAccessor {
	/**
	 * Placeholder of the textarea
	 */
	public readonly placeholder = input<string>();

	/**
	 * Label of the textarea
	 */
	public readonly label = input<string>();

	/**
	 * Hint of the textarea
	 */
	public readonly hint = input<string>();

	/**
	 * Info of the textarea displayed with a question mark
	 * when this is defined as trailing icon
	 */
	public readonly info = input<string>();

	/**
	 * If the textarea is required
	 */
	public readonly required = input<boolean>();

	/**
	 * If the textarea is disabled
	 */
	public readonly disabled = input(false);

	/**
	 * If the textarea is auto-sized
	 */
	public readonly autosize = input<boolean | undefined>(false);

	/**
	 * The initial number of rows
	 */
	public readonly rows = input<number>();

	/**
	 * The maximum number of rows
	 */
	public readonly maxRows = input<number>();

	/**
	 * The maximum length of the textarea
	 */
	public readonly maxLength = input<number | undefined>(99999999999);

	/**
	 * Emits the value of the input on change
	 */
	@Output() public change: EventEmitter<string> = new EventEmitter<string>();

	/**
	 * The internal data model
	 */
	private innerValue = "";

	/**
	 * Returns the value of the input
	 */
	public get value(): string {
		return this.innerValue;
	}

	/**
	 * Sets the value of the input
	 */
	public set value(v: string) {
		if (v !== this.innerValue) {
			this.innerValue = v;
			this.onChangeCallback(v);
			this.change.emit(this.value);
		}
	}

	public writeValue(obj: string): void {
		if (obj !== this.innerValue) {
			this.innerValue = obj;
		}
	}

	private onTouchedCallback: () => void = noop;
	// biome-ignore lint/suspicious/noExplicitAny: <explanation>
	private onChangeCallback: (_: any) => void = noop;

	// From ControlValueAccessor interface
	// biome-ignore lint/suspicious/noExplicitAny: <explanation>
	public registerOnChange(fn: any) {
		this.onChangeCallback = fn;
	}

	// From ControlValueAccessor interface
	// biome-ignore lint/suspicious/noExplicitAny: <explanation>
	public registerOnTouched(fn: any) {
		this.onTouchedCallback = fn;
	}
}
